'use client';

import dynamic from 'next/dynamic';
import { DoorOpen } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Component, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import type { VideoSource } from '@/components/ui/visions/video/media';
import { entryHref, resolveRoom, roomHref, type HandBuiltRoom, type RoomEntry, type RoomOptions, type RoomSection, type RoomSpec } from '@/components/ui/rooms/resolve';
import { useTenant } from '@/contexts/TenantContext';
import '@/app/visions/visions.css';
import './room-stage.css';
import { RoomClose } from '@/components/ui/rooms/close';
import { readRingSize } from '@/components/ui/rooms/ring/geometry';
import { WALL_LOOKS, type WallLook } from '@/components/ui/rooms/panoramic/looks';

/** A catalogue entry's room, as its page needs it. */
export interface Room {
  /** The room's own address: the entry's address with /room on the end. */
  href: string;
  /** The entry's page, where closing the room leads. */
  entry: string;
  /** The work's name, for the stage's label. */
  title: string;
  /** Show the live canvas immediately, without a photographic loading cover. */
  immediate?: boolean;
  /**
   * Plays by itself in the entry's page, at full quality, once the stage comes near the viewport:
   * the rooms built around one film (Muraqqa Reality's wall). The rooms written for one work, with
   * their models and several large films, wait for "Start experience".
   */
  plays?: boolean;
  /**
   * The room for the page's stage. Not `full`, it plays by itself with no controls; `full`, the
   * same room is the experience proper. Client only.
   */
  live: (full: boolean) => ReactNode;
}

const VisionsBackdrop = dynamic(() => import('@/components/ui/visions/VisionsBackdrop'), { ssr: false, loading: () => null });
const SavingAestheticsBackdrop = dynamic(() => import('@/components/ui/savingaesthetics/SavingAestheticsBackdrop'), { ssr: false, loading: () => null });
const Tisa2Backdrop = dynamic(() => import('@/components/ui/tisa2/Tisa2Backdrop'), { ssr: false, loading: () => null });
const TisaBackdrop = dynamic(() => import('@/components/ui/savingaesthetics/tisa/TisaBackdrop'), { ssr: false, loading: () => null });
const MemememoryBackdrop = dynamic(() => import('@/components/ui/memememory/MemememoryBackdrop'), { ssr: false, loading: () => null });
const EasternPromisesBackdrop = dynamic(() => import('@/components/ui/easternpromises/EasternPromisesBackdrop'), { ssr: false, loading: () => null });
// Loaded only when the live room will actually run, so a phone never downloads the renderer.
const CircularRepetitionRoom = dynamic(() => import('@/components/ui/portal/CircularRepetitionRoom'), { ssr: false, loading: () => null });
const RelicRoom = dynamic(() => import('@/components/ui/relic/RelicRoom'), { ssr: false, loading: () => null });
const PanoramicRoom = dynamic(() => import('@/components/ui/rooms/panoramic/PanoramicRoom'), { ssr: false, loading: () => null });

/** The rooms written for one work each have a way to play inside a page. */
const HAND_BUILT_LIVE: Record<HandBuiltRoom, (full: boolean) => ReactNode> = {
  visions: (full) => <VisionsBackdrop full={full} />,
  savingaesthetics: (full) => <SavingAestheticsBackdrop soft={false} full={full} />,
  tisa: (full) => <TisaBackdrop full={full} />,
  tisa2: (full) => <Tisa2Backdrop full={full} />,
  memememory: (full) => <MemememoryBackdrop full={full} />,
  easternpromises: (full) => <EasternPromisesBackdrop full={full} />,
  relic: (full) => <RelicRoom full={full} />,
};

/** A desktop with a pointer and no wish for stillness gets the live room; the rest keep the still. */
function canRunLive() {
  return (
    window.innerWidth >= 1024 &&
    !window.matchMedia('(pointer: coarse)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

interface Av1Film {
  src: string;
  /** The video codec alone, for decodingInfo; the <source> type adds the sound's. */
  codec: string;
  audio: string;
  width: number;
  height: number;
  bitrate: number;
  framerate: number;
}

/**
 * AV1 cuts of a template room's film, encoded from the work's own render: sharper than the
 * catalogue's film and smaller. Offered first, and only where the device decodes AV1 in hardware:
 * decoding a 4096-wide film in software would cost the laptop more than the smaller file saves.
 * Everyone else plays the catalogue's film. Keyed by entry slug. The bucket answers with CORS
 * headers, and Safari accepts its MP4 in WebGL (see visions/video/media.ts).
 */
const AV1_FILMS: Record<string, Av1Film> = {
  // The first 150 s of the 2019 Venice Biennale render, CRF 50: 68 MB where the WebM is 109 MB.
  'muraqqa-reality': {
    src: 'https://orkhan-media.fsn1.your-objectstorage.com/encoded/video/97/v2/muraqqa-reality-av1.mp4',
    codec: 'av01.0.12M.08',
    audio: 'mp4a.40.2',
    width: 4096,
    height: 902,
    bitrate: 3_700_000,
    framerate: 25,
  },
};

async function decodesInHardware(film: Av1Film): Promise<boolean> {
  try {
    const info = await navigator.mediaCapabilities?.decodingInfo({
      type: 'file',
      video: { contentType: `video/mp4; codecs="${film.codec}"`, width: film.width, height: film.height, bitrate: film.bitrate, framerate: film.framerate },
    });
    return !!info?.supported && !!info.powerEfficient;
  } catch {
    return false;
  }
}

/** A template room in the page's stage: the camera tour by itself, or, `full`, the experience proper. */
function TemplateLive({ spec, title, look, av1, full, appearance }: { spec: Extract<RoomSpec, { kind: 'template' }>; title: string; look?: WallLook; av1?: Av1Film; full: boolean; appearance?: 'legacy' | 'skin-zero' }) {
  // Decided after mount: this component is also rendered on the server, where there is no window.
  const [live, setLive] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLive(canRunLive());
  }, []);
  // Whether to offer the AV1 cut; null while the browser is asked. The video hook restarts the
  // film whenever its sources change, so the room waits for the answer instead of switching.
  const [hardwareAv1, setHardwareAv1] = useState<boolean | null>(av1 ? null : false);
  useEffect(() => {
    if (!av1) return undefined;
    let current = true;
    void decodesInHardware(av1).then((ok) => {
      if (current) setHardwareAv1(ok);
    });
    return () => {
      current = false;
    };
  }, [av1]);
  const sources = useMemo<VideoSource[]>(() => {
    const film = { src: spec.film, type: spec.film.split(/[?#]/)[0].toLowerCase().endsWith('.webm') ? 'video/webm' : 'video/mp4' };
    return av1 && hardwareAv1 ? [{ src: av1.src, type: `video/mp4; codecs="${av1.codec}, ${av1.audio}"` }, film] : [film];
  }, [spec.film, av1, hardwareAv1]);
  if ((!live && !full) || hardwareAv1 === null) return null;
  if (spec.template === 'ledRing') return <PanoramicRoom sources={sources} title={title} backdrop={!full} form="ring" size={readRingSize(spec.dimensions)} appearance={appearance} />;
  return <PanoramicRoom sources={sources} title={title} backdrop={!full} bend={look?.bend} edges={look?.edges} appearance={appearance} />;
}

/** The room a catalogue entry has, if it has one: written for it, or a template around its film. */
export function roomFor(section: RoomSection, entry: RoomEntry, options: RoomOptions & { appearance?: 'legacy' | 'skin-zero' } = {}): Room | null {
  const spec = resolveRoom(entry, options);
  if (!spec) return null;
  // The wall looks and AV1 cuts are tuned for Orkhan's films, found by his slugs.
  const ownTuning = options.handBuilt ?? true;
  return {
    href: roomHref(section, entry.slug),
    entry: entryHref(section, entry.slug),
    title: entry.title,
    immediate: spec.kind === 'portal',
    plays: spec.kind === 'template',
    live: spec.kind === 'portal' ? (full) => <CircularRepetitionRoom full={full} /> : spec.kind === 'handBuilt' ? HAND_BUILT_LIVE[spec.room] : (full) => <TemplateLive spec={spec} title={entry.title} look={ownTuning ? WALL_LOOKS[entry.slug] : undefined} av1={ownTuning ? AV1_FILMS[entry.slug] : undefined} full={full} appearance={options.appearance} />,
  };
}

/**
 * If the live room cannot run (a browser that refuses another WebGL context, a film that will
 * not decode) it is dropped and the stage is told, so the still takes its place; the page and
 * the way in are untouched.
 */
class LiveOrStill extends Component<{ children: ReactNode; onFail: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {
    this.props.onFail();
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * A still as the site's image optimiser serves it. The catalogue hands over the file as it was
 * uploaded, which can be several megabytes; a phone is sent a WebP no wider than a desktop screen.
 * Only a plain picture path on the site itself is rewritten; any other address is used as given.
 */
const lighter = (url: string) => (/^\/(?!\/|_next\/)[^?#]*\.(png|jpe?g|webp|avif)$/i.test(url) ? `/_next/image?url=${encodeURIComponent(url)}&w=1920&q=75` : url);
const still = (url: string, position = 'center') => ({ backgroundImage: `url(${JSON.stringify(lighter(url))})`, backgroundPosition: position });

/** The entry's cover photograph, behind its header. Held a little above centre, where the wall is. */
export function RoomCover({ poster }: { poster: string }) {
  return <div className="absolute inset-0 bg-contain bg-no-repeat" style={still(poster)} />;
}

/** Every room draws its controls once its film is running: that is when the experience has started. */
const RUNNING = '.visions-hud, .sa-hud, [data-portal-ready="true"], [data-room-ready="true"]';
/** A room already playing by itself in the stage: it can become the experience at once. */
const PLAYING = '.visions--backdrop-live, .sa--live, [data-portal-ready="true"], [data-room-ready="true"]';

/**
 * The room, large and straight under the entry's title. Until it is asked for, the stage shows
 * the entry's still and loads nothing of the room: a room playing by itself streamed 70 to
 * 100 MB of film and models in its first 15 seconds and kept the page busy for seconds (measured
 * on orkhan.art in October 2026). Rooms that draw at once are the exception and are always there.
 *
 * This section is the way in and the only loading screen. "Start experience" starts the room
 * here: a room already playing takes its controls and grows to fill the page's panel, with
 * nothing loaded twice. A room that is not ready yet loads out of sight while the button shows
 * how far it is, and opens when it can. The room's own address shows this same page and starts
 * by itself (`autoStart`); while the room is open the address is the room's, so Back closes it
 * and the address can be shared.
 */
export function RoomStage({ room, poster, autoStart = false, appearance = 'legacy' }: { room: Room; poster?: string; autoStart?: boolean; appearance?: 'legacy' | 'skin-zero' }) {
  const router = useRouter();
  const section = useRef<HTMLElement>(null);
  /** idle: the still, nothing loaded. starting: asked for, loading out of sight. open: filling the panel. */
  const [phase, setPhase] = useState<'idle' | 'starting' | 'open'>(autoStart ? (room.immediate ? 'open' : 'starting') : 'idle');
  /** A fresh room each time the experience is closed, so the stage goes back to its still. */
  const [round, setRound] = useState(0);
  /** What the button says while the room loads: how far it is, that it needs a press, or that it failed. */
  const [wait, setWait] = useState<{ percent: number | null; press: boolean; failed: string | null }>({ percent: null, press: false, failed: null });
  /** The room does not run by itself here (the device, or a failure): the still stands in. Known after mount. */
  const [fallback, setFallback] = useState(false);
  /** The stage has come near the viewport once; from then on a room that plays by itself is kept. */
  const [near, setNear] = useState(false);
  const pushed = useRef(false);
  /** The phase as the last render left it, for callbacks that outlive a render. */
  const phaseNow = useRef(phase);
  useEffect(() => {
    phaseNow.current = phase;
  }, [phase]);

  useEffect(() => {
    if (!room.plays) return undefined;
    // Read after mount: the server has no window to ask.
    const live = canRunLive();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFallback(!live);
    if (!live) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setNear(true);
      observer.disconnect();
    }, { rootMargin: '200px' });
    if (section.current) observer.observe(section.current);
    return () => observer.disconnect();
  }, [room.plays]);

  const failed = useCallback(() => {
    setFallback(true);
    // A room that failed while playing by itself gives way to the still. Asked for later, it is
    // mounted afresh: the boundary that caught this failure would otherwise show nothing for ever.
    // A room that draws at once is never unmounted by the still, so a fresh one would mount at
    // once and a failure that repeats (its code never arrived) would remount without end: it is
    // left as it fell, and the button says so.
    if (phaseNow.current === 'idle' && !room.immediate) {
      setRound((n) => n + 1);
      return;
    }
    // A room that threw while it was asked for, or while open, cannot be started in place: leave
    // the wait (or the emptied stage) and say so, instead of loading for ever.
    setPhase('idle');
    setWait({ percent: null, press: false, failed: 'The room could not start.' });
  }, [room.immediate]);

  const start = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    // A new tab or window still gets the room's own address.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const stage = section.current;
    // The browser wanted a press before it would play the film: this press is it.
    const gate = stage?.querySelector<HTMLButtonElement>('.sa-preload-enter');
    if (gate) {
      gate.click();
      return;
    }
    setWait({ percent: null, press: false, failed: null });
    setPhase((current) => (current === 'open' ? current : room.immediate || stage?.querySelector(PLAYING) ? 'open' : 'starting'));
  }, [room.immediate]);

  const close = useCallback(() => {
    if (pushed.current) {
      window.history.back();
      return;
    }
    // Opened at the room's own address: the stage closes here, then the entry's page takes over.
    setPhase('idle');
    setRound((n) => n + 1);
    router.replace(room.entry);
  }, [router, room.entry]);

  // Forward onto the room's step, after Back closed it: the browser shows this same page under
  // the room's address, so the room opens again in place, and Back or Close leads out as before.
  useEffect(() => {
    if (phase !== 'idle') return undefined;
    const onPop = () => {
      if (window.location.pathname !== room.href) return;
      pushed.current = true;
      setWait({ percent: null, press: false, failed: null });
      setPhase(room.immediate || section.current?.querySelector(PLAYING) ? 'open' : 'starting');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [phase, room.href, room.immediate]);

  // Asked for: the room loads out of sight; the button reports on it; it opens when its controls are drawn.
  useEffect(() => {
    if (phase !== 'starting') return undefined;
    const check = () => {
      const stage = section.current;
      if (!stage) return;
      if (stage.querySelector(RUNNING)) {
        setPhase('open');
        return;
      }
      const press = !!stage.querySelector('.sa-preload-enter');
      // A room asking for a press has not failed: the browser wants a gesture before it plays the
      // film, and the press on this button is that gesture.
      const failed = press ? null : stage.querySelector('.visions-loader-line--error, .sa-preload-error, .relic-load-error, [data-template-view="skin-zero-room-loading"] [role="alert"]')?.textContent?.trim() || null;
      const line = stage.querySelector('.visions-loader-line, .sa-preload-state, .relic-load-state, [data-template-view="skin-zero-room-loading"] [role="status"]')?.textContent ?? '';
      const found = line.match(/(\d+)\s*%/);
      const percent = found ? Number(found[1]) : null;
      setWait((now) => (now.percent === percent && now.press === press && now.failed === failed ? now : { percent, press, failed }));
    };
    check();
    const timer = window.setInterval(check, 150);
    return () => window.clearInterval(timer);
  }, [phase]);

  // Open: the stage covers the page's panel, the page behind it holds still, and the address is the room's.
  useEffect(() => {
    if (phase !== 'open') return undefined;
    const scroller = section.current?.closest<HTMLElement>('#landscape-content') ?? null;
    const overflow = scroller?.style.overflow ?? '';
    if (scroller) {
      scroller.scrollTo(0, 0);
      scroller.style.overflow = 'hidden';
    }
    const entry = window.location.pathname + window.location.search;
    if (window.location.pathname !== room.href) {
      window.history.pushState({ roomOpen: true }, '', room.href);
      pushed.current = true;
    }
    const onPop = () => {
      pushed.current = false;
      setPhase('idle');
      setRound((n) => n + 1);
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (scroller) scroller.style.overflow = overflow;
      // Closed by something other than Back: put the entry's address back without leaving a step
      // behind. A link out of the open room has already put the next page's address there; that
      // one is left alone.
      if (pushed.current) {
        pushed.current = false;
        if (window.location.pathname === room.href) window.history.replaceState(null, '', entry);
      }
    };
  }, [phase, room.href]);

  const open = phase === 'open';
  // At the room's own address the room was asked for before the page drew, so the stage covers the
  // panel from the start instead of growing to it when the room opens (a layout shift of 0.47).
  const covering = open || (autoStart && phase === 'starting');
  const label = wait.failed ? 'Could not load. Try again' : wait.press || phase === 'idle' ? 'Start experience' : `Loading${wait.percent === null ? '' : ` ${wait.percent}%`}`;
  const pressable = phase === 'idle' || wait.press || !!wait.failed;
  // A room built around a film plays by itself where the device runs it, from the moment the stage
  // comes near the viewport; the others load once asked for. Asked for, any room runs anywhere, and
  // a room that draws at once is always there.
  const mounted = phase !== 'idle' || !!room.immediate || (!!room.plays && near && !fallback);
  const cover = poster;
  return (
    <section
      ref={section}
      aria-label={`${room.title} immersive experience`}
      data-phase={phase}
      className={`room-stage isolate w-full overflow-hidden bg-black ${covering ? 'absolute inset-0 z-[60]' : 'relative mb-[12px] rounded-md'}`}
      // The stage's size is set on the element itself, so it holds whatever state the page's stylesheet is in.
      style={covering ? undefined : { height: '82vh', minHeight: 480 }}
    >
      {/* The still, under the room: it shows until the room draws over it, and wherever the room does not run. */}
      {cover && !room.immediate ? <div className="absolute inset-0 bg-contain bg-no-repeat" style={still(cover)} aria-hidden /> : null}
      {/* While it loads the room stays out of sight, so the button (over the still, where there is one) is what the visitor sees. */}
      <div className="absolute inset-0" style={phase === 'starting' && !room.immediate ? { opacity: 0, pointerEvents: 'none' } : undefined}>
        {/* Once open, the room's own controls carry the way back to this page. */}
        <RoomClose.Provider value={open ? close : null}>
          {mounted ? <LiveOrStill key={round} onFail={failed}>{room.live(phase !== 'idle')}</LiveOrStill> : null}
        </RoomClose.Provider>
      </div>
      {open ? null : (
        <a
          href={room.href}
          onClick={pressable ? (wait.failed ? () => window.location.reload() : start) : (event) => event.preventDefault()}
          aria-disabled={!pressable}
          aria-live="polite"
          className={`group absolute inset-0 z-10 flex items-center justify-center p-[15px] ${appearance === 'skin-zero' ? 'text-sm' : 'font-mono text-[10px]'} ${pressable ? '' : 'cursor-progress'}`}
          aria-label={label}
        >
          <span className={`inline-flex items-center gap-2 px-3 py-2 border border-gray-200 bg-white text-black transition-colors group-hover:bg-gray-100 ${appearance === 'skin-zero' ? 'rounded-lg font-medium' : 'uppercase rounded-sm'}`}>
            <DoorOpen className={`h-3.5 w-3.5 ${pressable ? '' : 'animate-pulse'}`} strokeWidth={1.5} aria-hidden />
            {label}
          </span>
        </a>
      )}
    </section>
  );
}

/**
 * A room shown by itself, for when its entry cannot be read from the catalogue: the stage alone
 * in the page's panel, started at once. A room written for one work needs nothing from its entry.
 */
export function RoomAlone({ slug, title }: { slug: string; title: string }) {
  const { isOrkhan } = useTenant();
  const room = useMemo(() => roomFor('installations', { slug, title, video: null }, { handBuilt: isOrkhan }), [slug, title, isOrkhan]);
  if (!room) return null;
  return (
    <div className="w-full bg-black relative h-full overflow-y-auto" style={{ scrollbarWidth: 'none' }} id="landscape-content">
      <RoomStage room={room} autoStart />
    </div>
  );
}
